import React, { useEffect, useState } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { supabase } from '@/integrations/supabase/client';
import { 
  Bug, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  Image as ImageIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { format } from 'date-fns';

interface BugReport {
  id: string;
  user_id: string | null;
  user_email: string | null;
  description: string;
  screenshot_url: string | null;
  page_url: string | null;
  user_agent: string | null;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
}

const STATUS_CONFIG = {
  open: { 
    label: 'Open', 
    color: 'bg-destructive/10 text-destructive',
    icon: AlertCircle,
  },
  in_progress: { 
    label: 'In Progress', 
    color: 'bg-warning/10 text-warning',
    icon: Clock,
  },
  resolved: { 
    label: 'Resolved', 
    color: 'bg-primary/10 text-primary',
    icon: CheckCircle,
  },
  closed: { 
    label: 'Closed', 
    color: 'bg-muted text-muted-foreground',
    icon: CheckCircle,
  },
};

export default function AdminErrorReporting() {
  const [reports, setReports] = useState<BugReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [editingNotes, setEditingNotes] = useState<string | null>(null);
  const [notesValue, setNotesValue] = useState('');

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    const { data } = await supabase.functions.invoke('admin-operations', {
      body: { action: 'getBugReports' },
    });

    if (data?.reports) {
      setReports(data.reports);
    }
    setLoading(false);
  };

  const updateStatus = async (reportId: string, newStatus: string) => {
    await supabase.functions.invoke('admin-operations', {
      body: {
        action: 'updateBugReport',
        data: { reportId, updates: { status: newStatus } },
      },
    });

    setReports(prev => 
      prev.map(r => r.id === reportId ? { ...r, status: newStatus as BugReport['status'] } : r)
    );
  };

  const saveNotes = async (reportId: string) => {
    await supabase.functions.invoke('admin-operations', {
      body: {
        action: 'updateBugReport',
        data: { reportId, updates: { admin_notes: notesValue } },
      },
    });

    setReports(prev =>
      prev.map(r => r.id === reportId ? { ...r, admin_notes: notesValue } : r)
    );
    setEditingNotes(null);
  };

  const filteredReports = filterStatus === 'all' 
    ? reports 
    : reports.filter(r => r.status === filterStatus);

  const stats = {
    total: reports.length,
    open: reports.filter(r => r.status === 'open').length,
    in_progress: reports.filter(r => r.status === 'in_progress').length,
    resolved: reports.filter(r => r.status === 'resolved').length,
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Error Reporting</h1>
            <p className="text-muted-foreground">Bug reports from users</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-card border rounded-lg p-4">
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-sm text-muted-foreground">Total Reports</p>
          </div>
          <div className="bg-card border rounded-lg p-4">
            <p className="text-2xl font-bold text-destructive">{stats.open}</p>
            <p className="text-sm text-muted-foreground">Open</p>
          </div>
          <div className="bg-card border rounded-lg p-4">
            <p className="text-2xl font-bold text-warning">{stats.in_progress}</p>
            <p className="text-sm text-muted-foreground">In Progress</p>
          </div>
          <div className="bg-card border rounded-lg p-4">
            <p className="text-2xl font-bold text-primary">{stats.resolved}</p>
            <p className="text-sm text-muted-foreground">Resolved</p>
          </div>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-4">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Reports</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Reports list */}
        {loading ? (
          <p className="text-muted-foreground">Loading...</p>
        ) : filteredReports.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Bug className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No bug reports yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredReports.map(report => {
              const isExpanded = expandedId === report.id;
              const StatusIcon = STATUS_CONFIG[report.status].icon;
              
              return (
                <div key={report.id} className="bg-card border rounded-lg overflow-hidden">
                  {/* Header row */}
                  <div
                    className="flex items-center gap-4 p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => setExpandedId(isExpanded ? null : report.id)}
                  >
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${STATUS_CONFIG[report.status].color}`}>
                      <StatusIcon className="w-3 h-3" />
                      {STATUS_CONFIG[report.status].label}
                    </span>
                    
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {report.description.slice(0, 100)}
                        {report.description.length > 100 && '...'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {report.user_email || 'Anonymous'} • {format(new Date(report.created_at), 'MMM d, yyyy h:mm a')}
                      </p>
                    </div>
                    
                    {report.screenshot_url && (
                      <div className="w-8 h-8 bg-muted rounded flex items-center justify-center">
                        <ImageIcon className="w-4 h-4 text-muted-foreground" />
                      </div>
                    )}
                    
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-muted-foreground" />
                    )}
                  </div>
                  
                  {/* Expanded content */}
                  {isExpanded && (
                    <div className="border-t bg-muted/30 p-4">
                      <div className="grid grid-cols-2 gap-6">
                        {/* Left column - Details */}
                        <div className="space-y-4">
                          <div>
                            <h4 className="text-sm font-medium mb-1">Description</h4>
                            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                              {report.description}
                            </p>
                          </div>
                          
                          {report.page_url && (
                            <div>
                              <h4 className="text-sm font-medium mb-1">Page URL</h4>
                              <a 
                                href={report.page_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                              >
                                {report.page_url.slice(0, 50)}...
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            </div>
                          )}
                          
                          {report.user_agent && (
                            <div>
                              <h4 className="text-sm font-medium mb-1">Browser</h4>
                              <p className="text-xs text-muted-foreground">
                                {report.user_agent.slice(0, 100)}...
                              </p>
                            </div>
                          )}
                          
                          {/* Status update */}
                          <div>
                            <h4 className="text-sm font-medium mb-1">Update Status</h4>
                            <Select
                              value={report.status}
                              onValueChange={(val) => updateStatus(report.id, val)}
                            >
                              <SelectTrigger className="w-40">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="open">Open</SelectItem>
                                <SelectItem value="in_progress">In Progress</SelectItem>
                                <SelectItem value="resolved">Resolved</SelectItem>
                                <SelectItem value="closed">Closed</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        {/* Right column - Screenshot & Notes */}
                        <div className="space-y-4">
                          {report.screenshot_url && (
                            <div>
                              <h4 className="text-sm font-medium mb-1">Screenshot</h4>
                              <a href={report.screenshot_url} target="_blank" rel="noopener noreferrer">
                                <img
                                  src={report.screenshot_url}
                                  alt="Bug screenshot"
                                  className="w-full h-40 object-cover rounded-lg border hover:opacity-90 transition-opacity"
                                />
                              </a>
                            </div>
                          )}
                          
                          {/* Admin notes */}
                          <div>
                            <h4 className="text-sm font-medium mb-1 flex items-center gap-1">
                              <MessageSquare className="w-3 h-3" />
                              Admin Notes
                            </h4>
                            {editingNotes === report.id ? (
                              <div className="space-y-2">
                                <Textarea
                                  value={notesValue}
                                  onChange={(e) => setNotesValue(e.target.value)}
                                  placeholder="Add internal notes..."
                                  rows={3}
                                />
                                <div className="flex gap-2">
                                  <Button size="sm" onClick={() => saveNotes(report.id)}>
                                    Save
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => setEditingNotes(null)}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div 
                                className="mt-1 p-2 bg-background rounded border text-sm text-muted-foreground min-h-[60px] cursor-pointer hover:bg-muted/50"
                                onClick={() => {
                                  setEditingNotes(report.id);
                                  setNotesValue(report.admin_notes || '');
                                }}
                              >
                                {report.admin_notes || (
                                  <span className="text-muted-foreground/50 italic">Click to add notes...</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
